import { useEffect, useState } from 'react';
import type { SmsDefaultPrompt } from '../api/types';
import {
  clearOauthReturn,
  clearPendingMarker,
  clearSmsDefaultReturn,
  messageForOauthError,
  peekOauthReturn,
  peekPendingMarker,
  peekSmsDefaultReturn,
  type PendingOauth,
} from './oauthReturn';

export type OauthOutcome =
  /** The callback appended ?oauth_error — the consent failed. */
  | { kind: 'error'; message: string; rawError: string; marker: PendingOauth | null }
  /**
   * No error param but a fresh pending marker — the consent PROBABLY succeeded
   * (success appends nothing). The screen must refetch and verify the inbox
   * actually reads healthy before celebrating. `smsDefault` is a first
   * RingCentral sign-in's default-sender question, asked once it reads healthy.
   */
  | { kind: 'returned'; marker: PendingOauth; smsDefault: SmsDefaultPrompt | null };

/**
 * One-shot per page load: whichever guarded screen mounts first after an OAuth
 * round trip claims the outcome. Peek in the state initializer (StrictMode
 * renders twice), clear in the effect.
 */
export function useOauthReturn(): OauthOutcome | null {
  const [outcome] = useState<OauthOutcome | null>(() => {
    const ret = peekOauthReturn();
    const marker = peekPendingMarker();
    if (ret?.error) {
      return {
        kind: 'error',
        message: messageForOauthError(ret.error),
        rawError: ret.error,
        marker,
      };
    }
    if (marker) return { kind: 'returned', marker, smsDefault: peekSmsDefaultReturn() };
    return null;
  });

  useEffect(() => {
    if (outcome) {
      clearOauthReturn();
      clearPendingMarker();
      clearSmsDefaultReturn();
    }
  }, [outcome]);

  return outcome;
}
